/**
 * Survey API Client
 *
 * @remarks
 * Cliente HTTP para /api/v1/survey/sessions.
 * Mismo patrón de autenticación que urban-entities-api:
 * axios + interceptor que lee el token del auth store.
 *
 * @module survey/services
 */

import axios, { type AxiosInstance } from 'axios';
import { useAuthStore } from '../../../auth/useAuthStore';
import type {
  CreateObservationPayload,
  CreateSessionPayload,
  SurveyCapturesPage,
  SurveyObservation,
  SurveySession,
  SurveySourceFile,
  UpdateObservationPayload,
} from '../types/survey.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';
const BACKEND_URL = API_URL.replace(/\/api\/v1$/, '');

// ─── Auth token helper ────────────────────────────────────────────────────────

function getAuthToken(): string | null {
  const storeToken = useAuthStore.getState().token;
  if (storeToken) return storeToken;

  try {
    const raw = localStorage.getItem('auth-storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { token?: string }; token?: string };
    return parsed.state?.token ?? parsed.token ?? null;
  } catch {
    return null;
  }
}

// ─── Axios instance ───────────────────────────────────────────────────────────

function createApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/survey/sessions`,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    config => {
      const token = getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    error => Promise.reject(error)
  );

  instance.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        // Token expired or invalid — caller handles redirect
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

const api = createApiInstance();

function createObsApiInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: `${API_URL}/survey/observations`,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(
    config => {
      const token = getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    },
    error => Promise.reject(error)
  );

  return instance;
}

const obsApi = createObsApiInstance();

// ─── Decimal normalizer ───────────────────────────────────────────────────────

/**
 * TypeORM serializes Postgres decimal/numeric columns as strings.
 * This normalizer coerces them to JS number so downstream consumers
 * (MapLibre, heading calculations) receive the expected type.
 */
function parseObservation(obs: SurveyObservation): SurveyObservation {
  return {
    ...obs,
    obsLat: Number(obs.obsLat),
    obsLon: Number(obs.obsLon),
    estimatedLat: obs.estimatedLat !== null ? Number(obs.estimatedLat) : null,
    estimatedLon: obs.estimatedLon !== null ? Number(obs.estimatedLon) : null,
    confidence: obs.confidence !== null ? Number(obs.confidence) : null,
  };
}

// ─── API client ───────────────────────────────────────────────────────────────

export const surveyApi = {
  /** GET /survey/sessions?projectId= */
  async listSessions(projectId: string): Promise<SurveySession[]> {
    const response = await api.get<SurveySession[]>('/', { params: { projectId } });
    return response.data;
  },

  /** GET /survey/sessions/:id */
  async getSession(sessionId: string): Promise<SurveySession> {
    const response = await api.get<SurveySession>(`/${sessionId}`);
    return response.data;
  },

  /** POST /survey/sessions */
  async createSession(payload: CreateSessionPayload): Promise<SurveySession> {
    const response = await api.post<SurveySession>('/', payload);
    return response.data;
  },

  /** POST /survey/sessions/:id/files — multipart */
  async uploadFile(
    sessionId: string,
    fileType: 'video' | 'gpx',
    file: File
  ): Promise<SurveySourceFile> {
    const form = new FormData();
    form.append('file', file);
    form.append('fileType', fileType);

    const response = await api.post<SurveySourceFile>(`/${sessionId}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /** POST /survey/sessions/:id/process */
  async triggerProcessing(sessionId: string, intervalMeters?: number): Promise<{ jobId: string }> {
    const response = await api.post<{ jobId: string }>(`/${sessionId}/process`, {
      intervalMeters,
    });
    return response.data;
  },

  /** GET /survey/sessions/:id/captures?page=&limit= */
  async listCaptures(sessionId: string, page = 1, limit = 500): Promise<SurveyCapturesPage> {
    const response = await api.get<SurveyCapturesPage>(`/${sessionId}/captures`, {
      params: { page, limit },
    });
    return response.data;
  },

  /** POST /survey/observations */
  async createObservation(payload: CreateObservationPayload): Promise<SurveyObservation> {
    const response = await obsApi.post<SurveyObservation>('/', payload);
    return parseObservation(response.data);
  },

  /** GET /survey/observations?captureId= */
  async listObservationsByCapture(captureId: string): Promise<SurveyObservation[]> {
    const response = await obsApi.get<SurveyObservation[]>('/', { params: { captureId } });
    return response.data.map(parseObservation);
  },

  /** GET /survey/observations?sessionId= */
  async listObservationsBySession(sessionId: string): Promise<SurveyObservation[]> {
    const response = await obsApi.get<SurveyObservation[]>('/', { params: { sessionId } });
    return response.data.map(parseObservation);
  },

  /** PATCH /survey/observations/:id — generic update (status, spatial placement, etc.) */
  async updateObservation(
    id: string,
    payload: UpdateObservationPayload
  ): Promise<SurveyObservation> {
    const response = await obsApi.patch<SurveyObservation>(`/${id}`, payload);
    return parseObservation(response.data);
  },

  /** PATCH /survey/observations/:id — confirm shorthand */
  async confirmObservation(id: string): Promise<SurveyObservation> {
    const response = await obsApi.patch<SurveyObservation>(`/${id}`, { status: 'confirmed' });
    return parseObservation(response.data);
  },

  /** DELETE /survey/observations/:id */
  async deleteObservation(id: string): Promise<void> {
    await obsApi.delete(`/${id}`);
  },
};

/** Returns the public URL for a capture image stored under /uploads/. */
export function getCaptureImageUrl(storageKey: string): string {
  return `${BACKEND_URL}/uploads/${storageKey}`;
}

// ─── Error helper ─────────────────────────────────────────────────────────────

export function getSurveyApiError(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response
  ) {
    const data = (error.response as { data?: { message?: string } }).data;
    if (data?.message) return data.message;
  }
  if (error instanceof Error) return error.message;
  return 'Error desconocido en SurveyAPI';
}
